import { combineReducers } from 'redux';

import { call, put, select, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import {
  BillingPartyBasic,
  BillingPartyListResponse,
  EBillFileDownloadInfoRequest,
  EBillFileDownloadInfoResponse,
  EBillFileDownloadInitOtpResponse,
} from 'generated/bffFinancialInfo/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import { decodeBase64, downloadFile, encodedBufferToUTF8 } from 'helpers/downloadHandler';
import { mimeTypeToExtension } from 'helpers/mimeType';
import {
  AppAction,
  createActionCreator,
  createActionType,
  createApiActionCreators,
  createLoadingStateReducer,
  createReducer,
  LoadingStatus,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { DownloadPartyRefNosPayload } from 'models/Billing';
import { ExternalUser } from 'models/context/ExternalUser';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { selectIsUserInternal } from 'saga/auth/ducks';
import { selectContextExternalUser } from 'saga/context/external/ducks';
import { getKahlActions } from 'saga/kahl/ducks';
import { DownloadDoDocumentActions } from 'saga/billingManagement/downloadDoDocument/ducks';

export interface UpdateInvoicesDownloadPartiesPayload {
  partyRefNos: DownloadPartyRefNosPayload[];
  isSelected: boolean;
}

/* STATE */
export interface AllInvoicesState {
  billingParties: BillingPartyBasic[];
  invoicesDownloadPartyRefNos: DownloadPartyRefNosPayload[];
  eBillFileDownloadInfo: EBillFileDownloadInfoRequest;
  eBillFileDownloadInfoResponse: EBillFileDownloadInfoResponse;
  phoneNoOtp: string;
  loadingStatus: LoadingStatus;
}

/* ACTION TYPES */
export enum AllInvoicesActionTypes {
  GetBillingPartyList = '@@AllInvoices/GET_BILLING_PARTY_LIST',
  PostEBillFileDownloadInfo = '@@AllInvoices/POST_EBILL_FILE_DOWNLOAD_INFO',
  PostEbillFileDownloadInitOtp = '@@AllInvoices/POST_EBILL_FILE_DOWNLOAD_INIT_OTP',
  GetFinancialDocumentsReport = '@@AllInvoices/GET_FINANCIAL_DOCUMENTS_REPORT',
  UpdateInvoicesDownloadPartiesSelection = '@@AllInvoices/UPDATE_INVOICES_DOWNLOAD_PARTIES_SELECTION',
  SetLoading = '@@AllInvoices/SET_LOADING',
}

/* ACTIONS */
export const getBillingPartyListAction = createApiActionCreators(AllInvoicesActionTypes.GetBillingPartyList);
export const postEBillFileDownloadInfoAction = createApiActionCreators(
  AllInvoicesActionTypes.PostEBillFileDownloadInfo
);
export const postEbillFileDownloadInitOtpAction = createApiActionCreators(
  AllInvoicesActionTypes.PostEbillFileDownloadInitOtp
);

export const getFinancialDocumentsReportAction = createApiActionCreators(
  AllInvoicesActionTypes.GetFinancialDocumentsReport
);
export const updateInvoicesDownloadPartiesSelectionAction = createActionCreator(
  AllInvoicesActionTypes.UpdateInvoicesDownloadPartiesSelection
);
export const setLoadingAction = createActionCreator(AllInvoicesActionTypes.SetLoading);

/* REDUCERS */
const initialState: AllInvoicesState = {
  billingParties: [],
  invoicesDownloadPartyRefNos: [],
  eBillFileDownloadInfo: {
    billMonth: 0,
    billYear: 0,
    caRefNoList: [],
    contextCpRefNo: '',
    detailedInvoice: false,
    fileFormat: '',
    billContentType: undefined,
  },
  eBillFileDownloadInfoResponse: {
    hashId: '',
  },
  phoneNoOtp: '',
  loadingStatus: LoadingStatus.loading,
};

const billingParties = createReducer(initialState.billingParties, {
  [AllInvoicesActionTypes.GetBillingPartyList]: {
    [RequestActionTypes.SUCCESS]: (_state: BillingPartyListResponse, payload: BillingPartyListResponse) =>
      payload.billingParties,
    [RequestActionTypes.FAILURE]: () => [],
  },
});

const invoicesDownloadPartyRefNos = createReducer(initialState.invoicesDownloadPartyRefNos, {
  [AllInvoicesActionTypes.GetBillingPartyList]: {
    [RequestActionTypes.SUCCESS]: () => [],
    [RequestActionTypes.FAILURE]: () => null,
  },
  [AllInvoicesActionTypes.UpdateInvoicesDownloadPartiesSelection]: (
    _state: DownloadPartyRefNosPayload[],
    payload: UpdateInvoicesDownloadPartiesPayload
  ) => {
    if (payload.isSelected) {
      // add newly selected parties
      return [..._state, ...payload.partyRefNos.filter((p) => !_state.some((s) => s.partyRefNo === p.partyRefNo))];
    }

    // remove newly unselected parties
    return _state.filter((p) => !payload.partyRefNos.some((s) => s.partyRefNo === p.partyRefNo));
  },
});

const eBillFileDownloadInfo = createReducer(initialState.eBillFileDownloadInfo, {
  [AllInvoicesActionTypes.PostEBillFileDownloadInfo]: {
    [RequestActionTypes.SUCCESS]: (_state: EBillFileDownloadInfoRequest, payload: EBillFileDownloadInfoResponse) =>
      payload.hashId,
    [RequestActionTypes.FAILURE]: () => null,
  },
});

const eBillFileDownloadInfoResponse = createReducer(initialState.eBillFileDownloadInfoResponse, {
  [AllInvoicesActionTypes.PostEBillFileDownloadInfo]: {
    [RequestActionTypes.SUCCESS]: (_state: EBillFileDownloadInfoRequest, payload: EBillFileDownloadInfoResponse) =>
      payload.hashId,
  },
});

const phoneNoOtp = createReducer(initialState.phoneNoOtp, {
  [AllInvoicesActionTypes.PostEbillFileDownloadInitOtp]: {
    [RequestActionTypes.SUCCESS]: (_state: string, payload: EBillFileDownloadInitOtpResponse) => payload.phoneNoOtp,
    [RequestActionTypes.FAILURE]: () => null,
    [RequestActionTypes.REQUEST]: () => null,
  },
});

const loadingStatus = createLoadingStateReducer(
  initialState.loadingStatus,
  {
    [AllInvoicesActionTypes.PostEbillFileDownloadInitOtp]: [
      RequestActionTypes.REQUEST,
      RequestActionTypes.SUCCESS,
      RequestActionTypes.FAILURE,
    ],
    [AllInvoicesActionTypes.GetBillingPartyList]: [
      RequestActionTypes.REQUEST,
      RequestActionTypes.SUCCESS,
      RequestActionTypes.FAILURE,
    ],
  },
  {
    [AllInvoicesActionTypes.PostEbillFileDownloadInitOtp]: (state: LoadingStatus, status: LoadingStatus) => status,
    [AllInvoicesActionTypes.GetBillingPartyList]: (state: LoadingStatus, status: LoadingStatus) => status,
  }
);

export default combineReducers<AllInvoicesState>({
  billingParties,
  invoicesDownloadPartyRefNos,
  eBillFileDownloadInfo,
  eBillFileDownloadInfoResponse,
  phoneNoOtp,
  loadingStatus,
});

/* SELECTORS*/
const selectAllInvoicesState = (state: AppState): AllInvoicesState => state.allInvoices;

export const selectAllInvoicesBillingParties = (state: AppState): BillingPartyBasic[] =>
  selectAllInvoicesState(state).billingParties || [];

export const selectSelectedRefNosToInvoicesDownload = (state: AppState): DownloadPartyRefNosPayload[] =>
  selectAllInvoicesState(state).invoicesDownloadPartyRefNos || [];

export const selectEBillFileDownloadInfoData = (state: AppState): EBillFileDownloadInfoRequest =>
  selectAllInvoicesState(state).eBillFileDownloadInfo;

export const selectEBillFileDownloadHashId = (state: AppState): string | undefined =>
  selectAllInvoicesState(state).eBillFileDownloadInfoResponse.hashId;

export const selectPhoneNoOtp = (state: AppState): string => selectAllInvoicesState(state).phoneNoOtp;

export const selectAllInvoicesLoading = (state: AppState): LoadingStatus => selectAllInvoicesState(state).loadingStatus;

/* SAGAS */
function* getBillingPartyList({ payload: cpRefNo }: AppAction<string>) {
  yield put(setLoadingAction(LoadingStatus.loading));

  const request: ApiClientRequest = {
    apiCall: call(api.getBillingPartyList, yield getApiUrl(environment.urlConfig.financialInfo), cpRefNo),
  };

  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getBillingPartyListAction.success(resp.data));

    if (resp.data.billingParties?.length === 0) {
      yield put(getBillingPartyListAction.failure());
      yield put(getKahlActions.request({ infoMessageIds: 'ID0859' }));
    }
  } else if (resp.status === 403) {
    yield put(getBillingPartyListAction.failure());
    yield put(getKahlActions.request({ infoMessageIds: 'ID0859' }));
  } else {
    yield put(getKahlActions.request({ infoMessageIds: 'ID0858' }));
  }
}

function* postEbillFileDownloadInitOtp({ payload }: AppAction<EBillFileDownloadInfoRequest>) {
  const selectedRefNosToInvoicesDownload: DownloadPartyRefNosPayload[] = yield select(
    selectSelectedRefNosToInvoicesDownload
  );
  const caRefNoList: string[] = [];

  selectedRefNosToInvoicesDownload.forEach((item: DownloadPartyRefNosPayload) => {
    if (item.partyType === 'CustomerAccount') {
      caRefNoList.push(`${item.partyRefNo!}`);
    }
  });

  const request: ApiClientRequest = {
    apiCall: call(api.postEBillFileDownloadInitOtp, yield getApiUrl(environment.urlConfig.financialInfo), caRefNoList),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    const ebillFileDownloadInitOtpResponseData: EBillFileDownloadInitOtpResponse = resp.data;

    if (ebillFileDownloadInitOtpResponseData.phoneNoOtp) {
      yield put(getKahlActions.request({ infoMessageIds: 'ID0766' }));
      yield put(postEbillFileDownloadInitOtpAction.success(ebillFileDownloadInitOtpResponseData));
    } else {
      yield put(
        postEBillFileDownloadInfoAction.request({
          phoneNoOtp: ebillFileDownloadInitOtpResponseData.phoneNoOtp,
          billMonth: payload.billMonth,
          billYear: payload.billYear,
          fileFormat: payload.fileFormat,
          caRefNoList: selectedRefNosToInvoicesDownload,
          detailedInvoice: payload.detailedInvoice,
          contextCpRefNo: payload.contextCpRefNo,
          billContentType: payload.billContentType,
        })
      );
    }
  } else {
    yield put(postEbillFileDownloadInitOtpAction.failure());
    yield put(getKahlActions.request({ infoMessageIds: 'UM-ID04' }));
  }
}

function* postEBillFileDownloadInfo({ payload }: AppAction<EBillFileDownloadInfoRequest>) {
  const selectedRefNosToInvoicesDownload: DownloadPartyRefNosPayload[] = yield select(
    selectSelectedRefNosToInvoicesDownload
  );
  const caRefNoList: string[] = [];

  selectedRefNosToInvoicesDownload.forEach((item: DownloadPartyRefNosPayload) => {
    if (item.partyType === 'CustomerAccount') {
      caRefNoList.push(`${item.partyRefNo!}`);
    }
  });

  const request: ApiClientRequest = {
    apiCall: call(
      api.postEBillFileDownloadInfo,
      yield getApiUrl(environment.urlConfig.financialInfo),
      payload.billMonth,
      payload.billYear,
      caRefNoList,
      payload.contextCpRefNo,
      payload.billContentType,
      payload.detailedInvoice,
      payload.fileFormat,
      payload.otpCode
    ),
  };

  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    const hashId: EBillFileDownloadInfoResponse = resp.data;

    yield put(postEBillFileDownloadInfoAction.success(hashId));
    yield put(DownloadDoDocumentActions.request(hashId));
  } else if (resp.status === 403) {
    yield put(getKahlActions.request({ infoMessageIds: 'ID0859' }));
    yield put(postEBillFileDownloadInfoAction.failure());
  } else {
    yield put(getKahlActions.request({ infoMessageIds: 'ID0013' }));
    yield put(postEBillFileDownloadInfoAction.failure());
    yield put(setLoadingAction(LoadingStatus.failure));
  }
}

function* getFinancialDocumentsReport({ payload: dateFrom }: AppAction<Date>) {
  const selectedRefNosToInvoicesDownload: DownloadPartyRefNosPayload[] = yield select(
    selectSelectedRefNosToInvoicesDownload
  );
  const isInternal: boolean = yield select(selectIsUserInternal);
  const contextExtUser: ExternalUser = yield select(selectContextExternalUser);
  const cpRefNo = isInternal ? contextExtUser?.cpRefNo : undefined;
  let caRefNoList = '';

  selectedRefNosToInvoicesDownload.forEach((item: DownloadPartyRefNosPayload) => {
    if (item.partyType === 'CustomerAccount') {
      caRefNoList = caRefNoList.concat(`${item.partyRefNo!},`);
    }
  });

  const request: ApiClientRequest = {
    apiCall: call(
      api.getFinancialDocumentReport,
      yield getApiUrl(environment.urlConfig.financialInfo),
      caRefNoList.substring(0, caRefNoList.length - 1),
      dateFrom,
      cpRefNo
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    const { fileContent, mimeType } = resp.data;
    const decoded =
      mimeType === 'text/csv'
        ? encodedBufferToUTF8(decodeBase64(fileContent.toString()), 'UTF-8', true)
        : decodeBase64(fileContent.toString());

    downloadFile(decoded, `faktury_vse.${mimeTypeToExtension(mimeType)}`, mimeType);
  } else {
    yield put(getFinancialDocumentsReportAction.failure());
    yield put(getKahlActions.request({ infoMessageIds: 'ID0013' }));
  }
}

/* EXPORT */
export function* allInvoicesSaga() {
  yield takeLatest(
    createActionType(AllInvoicesActionTypes.GetBillingPartyList, RequestActionTypes.REQUEST),
    getBillingPartyList
  );

  yield takeLatest(
    createActionType(AllInvoicesActionTypes.PostEBillFileDownloadInfo, RequestActionTypes.REQUEST),
    postEBillFileDownloadInfo
  );

  yield takeLatest(
    createActionType(AllInvoicesActionTypes.PostEbillFileDownloadInitOtp, RequestActionTypes.REQUEST),
    postEbillFileDownloadInitOtp
  );

  yield takeLatest(
    createActionType(AllInvoicesActionTypes.GetFinancialDocumentsReport, RequestActionTypes.REQUEST),
    getFinancialDocumentsReport
  );
}
